import { CropBox } from "@wss/image-capture/utilities";
import React, { useEffect } from "react";
import {
  TransformWrapper,
  TransformComponent,
  useTransformEffect,
  ReactZoomPanPinchState,
  useTransformContext,
} from "react-zoom-pan-pinch";

export interface Dimensions {
  width: number;
  height: number;
}

interface ImageSelectCropperProps {
  imageUrl: string;
  imageSize: Dimensions;
  viewportSize: Dimensions;
  onCropBoxChange: (newCropBox: CropBox) => void;
}

interface CroppableImageElementProps {
  imageUrl: string;
  imageSize: Dimensions;
  viewportSize: Dimensions;
  initialScale: number;
  onCropBoxChange: (newCropBox: CropBox) => void;
}

function calculateInitialScale(
  boxBounds: Dimensions,
  imageSize: Dimensions,
): number {
  // We need to "zoom" in on the image so it fills the whole bounding box, cropping either height or width depending on image orientation

  if (imageSize.height >= imageSize.width) {
    // it's a portrait-mode photo (or already square).  Base the zoom factor on the width
    if (imageSize.width >= boxBounds.width) {
      //  default case - image is larger than screen, zoom=1 means "shrink to fit width"
      return 1;
    }
    // otherwise, the image is smaller than the screen - we should zoom in to fit it.
    return boxBounds.width / imageSize.width;
  }

  // Otherwise, it's landscape, so it needs to zoom in to match the vertical dimension
  if (imageSize.width >= boxBounds.width) {
    // it's wider than the viewport, so zoom factor can be a simple ratio of width/height
    return imageSize.width / imageSize.height;
  }
  // otherwise it's tiny - zoom factor needs to make the vertical dimension fill the bounding box
  return boxBounds.height / imageSize.height;
}

function calculateCropBox(
  state: ReactZoomPanPinchState,
  imageSize: Dimensions,
  viewportSize: Dimensions,
  initialScale: number,
): CropBox {
  // The UI component can be a bit fuzzy with its scaling, etc - make sure we have some hard limits to round up/down to
  const maxDimension = Math.min(imageSize.width, imageSize.height);

  // If the image was smaller than the viewport, we calculated a special initial scale - which will otherwise be 1
  // Need to factor that out to get a real scale value that can be applied to the original image dimensions for sizing
  const dimensionScale = state.scale / initialScale;

  // Once we've adjusted for the smaller-than-window source images, the scale value is a simple ratio of the image's original width
  // so we can apply that to get an appropriate size for the square - but no bigger than our calculated max
  const squareSize = Math.round(
    Math.min(maxDimension, imageSize.width / dimensionScale),
  );

  // The x/y positions represent the screen coordinates - in screen pixels - of the top left of the full image, which will be
  // off-screen if there has been any zooming.  The scale factor for converting this to a coordinate within the image is again
  // dependent on whether or not the original image was smaller than the screen.
  // If it was smaller (initialScale was > 1), we can just use the same scale as the dimensions.
  let positionScale = state.scale;
  if (initialScale === 1) {
    // but if the image was larger than the screen, scale=1 means "fit width to viewport" - and we need to convert that to a
    // actual pixel scale value in order to interpret the x/y coordinates.  Image-pixels per screen-pixels:
    const initialPixelScale = imageSize.width / viewportSize.width;
    // Now we can convert the current raw t the same units and apply it to the x/y positions:
    positionScale = state.scale / initialPixelScale;
  }
  // Both the x and y coordinates are negative offsets, scaled by the component's current scale value (not our adjusted "realScale")
  // Need to flip them and make sure they are in bounds (raw values can sometimes be >0 just because)
  const xPos = Math.max(0, 0 - state.positionX / positionScale);
  const yPos = Math.max(0, 0 - state.positionY / positionScale);

  // ... and make sure they don't make the box go out of bounds
  const cropBox = {
    x: Math.min(imageSize.width - squareSize, xPos),
    y: Math.min(imageSize.height - squareSize, yPos),
    width: squareSize,
    height: squareSize,
  };
  return cropBox;
}

const CroppableImageElement = ({
  imageUrl,
  imageSize,
  viewportSize,
  initialScale,
  onCropBoxChange,
}: CroppableImageElementProps) => {
  const context = useTransformContext();
  useTransformEffect(({ state /* , instance */ }) => {
    onCropBoxChange(
      calculateCropBox(state, imageSize, viewportSize, initialScale),
    );
  });

  useEffect(() => {
    // Make sure we calculate the crop dimensions at least once when mounting
    onCropBoxChange(
      calculateCropBox(
        context.transformState,
        imageSize,
        viewportSize,
        initialScale,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <img src={imageUrl} alt="crop" />;
};

const ImageSelectCropper = ({
  imageUrl,
  imageSize,
  viewportSize,
  onCropBoxChange,
}: ImageSelectCropperProps) => {
  const initialScale = calculateInitialScale(viewportSize, imageSize);
  return (
    <TransformWrapper
      centerOnInit={initialScale === 1}
      initialScale={initialScale}
      minScale={initialScale}
      disablePadding
      limitToBounds
    >
      <TransformComponent
        wrapperStyle={{
          width: viewportSize.width,
          height: viewportSize.height,
        }}
      >
        <CroppableImageElement
          imageUrl={imageUrl}
          imageSize={imageSize}
          viewportSize={viewportSize}
          initialScale={initialScale}
          onCropBoxChange={onCropBoxChange}
        />
      </TransformComponent>
    </TransformWrapper>
  );
};

export default ImageSelectCropper;
